import React, { useRef, useState, useEffect } from "react";
import { Box, useMediaQuery } from "@material-ui/core";
import { useVerticalNav } from "../../../Contexts/VerticalNavContext";
import {
  GetAgentCommissionSummary,
  GetAllAgentsServices,
  GetAllBranches,
  GetAllDevelopersContactsAPI,
  lookupItemsGetId,
} from "../../../Services";
import {
  CustomBadge,
  CustomBreadCrumb,
  CustomPagination,
  PageTitle,
  CustomExpandCard,
  CustomExpandSkeleton,
  NoDataFound,
  CustomSideMenu,
  ReskinningInputs,
  CustomIconButton,
  CustomAsyncAutocomplete,
  CustomDateRangePicker,
} from "../../../Components";
import AgentCommissionCardSummary from "./AgentCommissionCardSummary/AgentCommissionCardSummary";
import AgentCommissionSideMenu from "./CommissionTable/AgentCommissionSideMenu/AgentCommissionSideMenu";
import AgentCommissionNestedMenu from "./CommissionTable/AgentCommissionSideMenu/AgentCommissionNestedMenu/AgentCommissionNestedMenu";
import CommissionTable from "./CommissionTable/CommissionTable";

import useFetchData from "../../../Hooks/useFetchData";
import { useSelectedTheme, useTranslate } from "../../../Hooks";

import useStyles from "./styles";
import { SettingsFour } from "../../../assets/icons";
import {
  GetAllTeamLeadUsers,
  GetAllTeamManagerUsers,
} from "../../../Services/userServices";
import { StaticLookupsIds } from "../../../assets/json/StaticLookupsIds";
import moment from "moment";

export function NewAgentCommissionView() {
  const { translate } = useTranslate("AgentCommissionView");
  const {
    theme: { palette },
  } = useSelectedTheme();
  const styles = useStyles();
  const [params, setParams] = useState({ pageSize: 25, pageIndex: 1 });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isNestedMenuOpen, setIsNestedMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState(params.pageIndex);
  const [refetchTableData, setRefetchTableData] = useState(false);
  const [showFiltersSection, setShowFiltersSection] = useState(true);
  const dropPagination = useRef(null);
  const stickyPagination = useRef(null);
  const { setStickyBottomBox } = useVerticalNav();
  const [tabs, setTabs] = React.useState([
    ...(selectedTransaction?.invoices || []),
  ]);

  const dateRangeDefault = {
    startDate: null,
    endDate: null,
    key: "selection",
    selectedDateType: 1,
  };
  const [dateFilter, setDateFilter] = useState(dateRangeDefault);

  const isSmallScreen = useMediaQuery("(max-width:960px)");

  useEffect(() => {
    // show invoices in tabs on smaller screens if transactions doesnt have invoices
    if (isSmallScreen && tabs.length === 0) {
      setTabs([{ label: translate("invoice") + ` 1`, id: 1 }]);
    }
  }, [isSmallScreen]);

  const handleAddTab = () => {
    if (!tabs.length) {
      setTabs([...tabs, { label: `Invoice ${tabs.length + 1}`, id: 1 }]);
    }
  };

  useEffect(() => {
    if (selectedTransaction?.invoices?.length > 0) {
      setTabs(
        selectedTransaction.invoices.map((invoice, index) => ({
          ...invoice,
          label: `Invoice ${invoice.agentAchievementCommissionInvoiceId}`,
          id: index + 1,
        }))
      );
    } else {
      setTabs([]);
    }
  }, [selectedTransaction?.invoices]);

  const {
    data: { result, totalCount },
    loading,
  } = useFetchData(GetAgentCommissionSummary, params);

  const onCloseSideMenu = () => {
    setIsMenuOpen(false);
    setIsNestedMenuOpen(false);
    setSelectedTransaction(null);
  };
  const onOpenSideMenu = (selectedTransaction) => {
    setIsMenuOpen(true);
    setSelectedTransaction(selectedTransaction);
  };
  const onOpenNestedSideMenu = () => {
    setIsNestedMenuOpen(true);
  };

  useEffect(() => {
    if (result?.length) {
      setStickyBottomBox(
        <CustomPagination
          hideInMobile
          ref={stickyPagination}
          currentPage={params.pageIndex}
          inputValue={inputValue}
          setInputValue={setInputValue}
          totalItems={totalCount}
          itemsPerPage={params.pageSize}
          onPageChange={(page) =>
            setParams((prev) => ({ ...prev, pageIndex: page }))
          }
          onItemsPerPageChange={(items) =>
            setParams((prev) => ({ ...prev, pageSize: items }))
          }
          isLoading={loading}
          isSticky
        />
      );
    }

    return () => {
      setStickyBottomBox(null);
    };
  }, [loading]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!stickyPagination.current) return;
          if (entry.isIntersecting) {
            stickyPagination.current.style.display = "none";
          } else {
            stickyPagination.current.style.display = "block";
          }
        });
      },
      {
        root: null, // Default to viewport
        threshold: 1, // Adjust this threshold as needed
      }
    );

    // Start observing the dropPagination element
    if (dropPagination.current) {
      observer.observe(dropPagination.current);
    }

    // Cleanup observer on component unmount
    return () => {
      if (dropPagination.current) {
        observer.unobserve(dropPagination.current);
      }
    };
  }, []);
  return (
    <>
      <CustomBreadCrumb
        breadcrumbs={[
          {
            label: translate("bread-crump-parent"),
            nonClickable: true,
          },
        ]}
      >
        <CustomBadge
          Style={{
            padding: "4px 8px",
            borderRadius: "6px",
            lineHeight: "20px",
          }}
          label={translate("bread-crump-current")}
          BackgroundColor={palette.breadcrumbs.brand_bg_hover}
          BorderColor={palette.breadcrumbs.brand_bg_hover}
          Color={palette.breadcrumbs.brand_fg_hover}
        />
      </CustomBreadCrumb>
      <PageTitle
        wrapperClasses={styles.title}
        title={translate("page-title")}
        subTitle={translate("page-subtitle")}
        hideDivider
      />
      <Box display={"flex"} flexDirection={"column"} gridGap={"24px"}>
        <Box className={styles.filterWrapper}>
          <Box className={styles.filtersButtons}>
            <CustomIconButton
              variant="outlined"
              size="md"
              boxShadow="none"
              color="secondary"
              onClick={() => setShowFiltersSection((prev) => !prev)}
            >
              <SettingsFour
                width="20"
                height="20"
                fill={palette.button.secondary_fg}
              />
            </CustomIconButton>
          </Box>
          {showFiltersSection && (
            <Box className={styles.filterSection}>
              <Box className={styles.filtersContainer}>
                <CustomAsyncAutocomplete
                  rootCustomClasses={styles.inputwrapper}
                  className={styles.autoCompleteInput}
                  onFetchData={GetAllBranches}
                  chipsLabel={(option) => option.branchName || ""}
                  displayLabel={(option) => option.branchName || ""}
                  getOptionSelected={(option, value) =>
                    option?.branchId === value?.branchId
                  }
                  onChange={(_e, value) => {
                    setParams({ ...params, branchId: value?.branchId });
                  }}
                  mapper={(data) => {
                    return data.map((item) => item);
                  }}
                  queryKey={"branchName"}
                  placeholder={translate("branch-name")}
                  filter={{ branchName: "" }}
                  renderOption={(option) => option?.branchName}
                  refetchOnSearch
                />
                <CustomAsyncAutocomplete
                  rootCustomClasses={styles.inputwrapper}
                  className={styles.autoCompleteInput}
                  placeholder={translate("team-leader")}
                  onFetchData={GetAllTeamLeadUsers}
                  chipsLabel={(option) =>
                    option.teamLeadName || option.teamLeadName || ""
                  }
                  onChange={(_e, value) => {
                    setParams({
                      ...params,
                      teamLeaderId: value?.applicationUserId,
                    });
                  }}
                  displayLabel={(option) =>
                    option.teamLeadName || option.teamLeadName || ""
                  }
                  getOptionSelected={(option, value) =>
                    value.applicationUserId === option.applicationUserId
                  }
                  mapper={(data) => data}
                  filter={{
                    pageIndex: 1,
                    pageSize: 1000,
                    search: "",
                  }}
                  queryKey={"search"}
                  refetchOnSearch
                />
                <CustomAsyncAutocomplete
                  rootCustomClasses={styles.inputwrapper}
                  className={styles.autoCompleteInput}
                  placeholder={translate("sales-manager")}
                  onFetchData={GetAllTeamManagerUsers}
                  chipsLabel={(option) => option.teamManagerName}
                  onChange={(_e, value) => {
                    setParams({
                      ...params,
                      teamManagerId: value?.applicationUserId,
                    });
                  }}
                  displayLabel={(option) => option.teamManagerName}
                  getOptionSelected={(option, value) =>
                    value.applicationUserId === option.applicationUserId
                  }
                  mapper={(data) => data}
                  filter={{
                    pageIndex: 1,
                    pageSize: 1000,
                    search: "",
                  }}
                  queryKey={"search"}
                  refetchOnSearch
                />
                <CustomAsyncAutocomplete
                  rootCustomClasses={styles.inputwrapper}
                  className={styles.autoCompleteInput}
                  placeholder={translate("agent-name")}
                  onFetchData={GetAllAgentsServices}
                  filter={{ pageIndex: 0, pageSize: 1000 }}
                  displayLabel={(option) => option?.agentName ?? ""}
                  renderOption={(option) => option?.agentName}
                  chipsLabel={(option) => {
                    return option?.agentName;
                  }}
                  onChange={(_e, value) => {
                    setParams({
                      ...params,
                      agentId: value?.agentId,
                    });
                  }}
                  getOptionSelected={(option, value) =>
                    option?.agentId === value?.agentId
                  }
                  mapper={(data) => data}
                  queryKey={"agentName"}
                  refetchOnSearch
                />
                <CustomAsyncAutocomplete
                  rootCustomClasses={styles.inputwrapper}
                  className={styles.autoCompleteInput}
                  placeholder={translate("developer-name")}
                  selectedValues={""}
                  onChange={(_e, value) =>
                    setParams({
                      ...params,
                      developerId: value?.developerId,
                    })
                  }
                  mapper={(data) =>
                    data.map((item) => {
                      const developerContact =
                        item?.contact && JSON.parse(item.contact);
                      return {
                        developerId: item.contactsId,
                        developerName: developerContact?.contact?.company_name,
                      };
                    })
                  }
                  queryKey={"search"}
                  onFetchData={GetAllDevelopersContactsAPI}
                  filter={{ pageIndex: 1, pageSize: 1000 }}
                  displayLabel={(option) => option?.developerName ?? ""}
                  renderOption={(option) => option?.developerName}
                  chipsLabel={(option) => {
                    return option?.developerName;
                  }}
                  getOptionSelected={(option, value) =>
                    option?.developerId === value?.developerId
                  }
                  refetchOnSearch
                />
                <CustomAsyncAutocomplete
                  rootCustomClasses={styles.inputwrapper}
                  className={styles.autoCompleteMedia}
                  placeholder={translate("media-name")}
                  displayLabel={(option) => option?.mediaDetailsName ?? ""}
                  renderOption={(option) => {
                    return option?.mediaDetailsName;
                  }}
                  chipsLabel={(option) => option?.mediaDetailsName}
                  getOptionSelected={(option, value) =>
                    option?.mediaDetailsId === value?.mediaDetailsId
                  }
                  filter={{
                    lookupTypeId: StaticLookupsIds.MediaDetail,
                    pageIndex: 1,
                    pageSize: 10000,
                  }}
                  onFetchData={lookupItemsGetId}
                  mapper={(data) =>
                    data.map(({ lookupItemName, lookupItemId }) => ({
                      mediaDetailsId: lookupItemId,
                      mediaDetailsName: lookupItemName,
                    }))
                  }
                  onChange={(_e, value) =>
                    setParams({
                      ...params,
                      mediaName: value?.mediaDetailsName,
                    })
                  }
                  withoutSearchButton
                  refetchOnSearch
                />
                <ReskinningInputs
                  inputPlaceholder={translate("lead-id")}
                  type={"number"}
                  overInputIcon
                  fieldClasses={styles.searchInput}
                  onInputChanged={(e) => {
                    if (e.target.value === "") {
                      setParams({
                        ...params,
                        leadId: 0,
                      });
                      return;
                    }
                    setParams({
                      ...params,
                      leadId: e.target.value,
                    });
                  }}
                />
                <ReskinningInputs
                  inputPlaceholder={translate("transaction-id")}
                  overInputIcon
                  fieldClasses={styles.searchInput}
                  onInputChanged={(e) => {
                    if (e.target.value === "") {
                      setParams({
                        ...params,
                        transactionId: 0,
                      });
                      return;
                    }
                    setParams({
                      ...params,
                      transactionId: e.target.value,
                    });
                  }}
                />
              </Box>
              <Box className={styles.dateFilterContainer}>
                <Box className={styles.dateFilter}>
                  {translate("closing-date")}
                </Box>
                <CustomDateRangePicker
                  onClearClicked={() => {
                    setDateFilter(dateRangeDefault);
                    setParams({
                      ...params,
                      closingDateFrom: "",
                      closingDateTo: "",
                    });
                  }}
                  ranges={[dateFilter]}
                  onDateChanged={(selectedDate) => {
                    setDateFilter((item) => ({
                      ...item,
                      startDate:
                        selectedDate.selection &&
                        selectedDate.selection.startDate,
                      endDate: !selectedDate.selection?.endDate
                        ? null
                        : new Date(
                            moment(
                              selectedDate.selection &&
                                selectedDate.selection.endDate
                            ).endOf("day")
                          ),
                      key: "selection",
                    }));
                  }}
                  emptyLabel="dd/mm/yyyy"
                  displayFormat="MMM D, YYYY"
                  onSave={(ranges) => {
                    setParams({
                      ...params,
                      closingDateFrom: ranges.startDate,
                      closingDateTo: ranges.endDate,
                    });
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
        {loading &&
          Array.from({ length: 7 }).map((_, index) => (
            <CustomExpandSkeleton key={index} />
          ))}

        {!loading &&
          result?.length > 0 &&
          result.map((agent) => (
            <CustomExpandCard
              CardSummary={<AgentCommissionCardSummary data={agent} />}
              key={agent.agentId}
              CardExpandContent={(isExpanded) => (
                <CommissionTable
                  params={params}
                  isExpanded={isExpanded}
                  agentId={agent.agentId}
                  onOpenSideMenu={onOpenSideMenu}
                  refetch={refetchTableData}
                />
              )}
            />
          ))}
        <div ref={dropPagination}>
          {!loading && !!result?.length > 0 && (
            <CustomPagination
              hideInMobile
              currentPage={params.pageIndex}
              inputValue={inputValue}
              setInputValue={setInputValue}
              totalItems={totalCount}
              itemsPerPage={params.pageSize}
              onPageChange={(page) =>
                setParams((prev) => ({ ...prev, pageIndex: page }))
              }
              onItemsPerPageChange={(items) =>
                setParams((prev) => ({ ...prev, pageSize: items }))
              }
              isLoading={loading}
            />
          )}
        </div>

        {!loading && !result?.length > 0 && (
          <NoDataFound
            wrapperClass={styles.noDataWrapper}
            title="No data found"
            subTitle=""
          />
        )}

        <CustomSideMenu open={isMenuOpen} onClose={onCloseSideMenu}>
          {(props) => (
            <Box className={styles.sideMenuContainer}>
              <AgentCommissionSideMenu
                selectedTransaction={selectedTransaction}
                handleSideMenuOpen={onOpenNestedSideMenu}
                handleAddTab={handleAddTab}
                tabs={tabs}
                setTabs={setTabs}
                isOpen={isNestedMenuOpen}
                refetchdata={() => setRefetchTableData(!refetchTableData)}
                {...props}
              />
              <AgentCommissionNestedMenu
                selectedTransaction={selectedTransaction}
                {...props}
                isOpen={isNestedMenuOpen && !isSmallScreen}
                tabs={tabs}
                setTabs={setTabs}
                refetchdata={() => setRefetchTableData(!refetchTableData)}
              />
            </Box>
          )}
        </CustomSideMenu>
      </Box>
    </>
  );
}
